import {
    CognitoIdentityProviderClient,
    GlobalSignOutCommand,
    NotAuthorizedException,
} from '@aws-sdk/client-cognito-identity-provider';

import { graphql } from '__generated__';

import type { AuthUser } from 'common/auth/types';
import { CIError, ErrorSource } from 'common/error/error_util';
import gqlClient from 'common/graphql/client';
import { CILogger } from 'common/log/log.provider';
import Sentry from 'common/sentry/sentry';

const SIGN_OUT_MUTATION = graphql(`
    mutation SignOut {
        signOut
    }
`);

export async function revokeBackendSession(authUser: AuthUser | null): Promise<void> {
    try {
        await gqlClient.mutate({ mutation: SIGN_OUT_MUTATION });
    } catch (err) {
        const error = CIError.fromGraphQLError(err);
        CILogger.getInstance().error('signOut mutation failed', error, { subjectId: authUser?.pk });
        Sentry?.captureException(error);
    }
}

export async function globalSignOutCognito(accessToken: string | undefined, authUser: AuthUser | null): Promise<void> {
    if (!accessToken) {
        return;
    }

    try {
        const region = process?.env?.REACT_APP_AWS_HOME_REGION || 'us-east-1';
        const client = new CognitoIdentityProviderClient({ region });
        await client.send(new GlobalSignOutCommand({ AccessToken: accessToken }));
    } catch (err) {
        if (err instanceof NotAuthorizedException && /revoked/i.test((err as Error)?.message ?? '')) {
            CILogger.getInstance().log('GlobalSignOut found an already-revoked session', err, {
                subjectId: authUser?.pk,
            });

            return;
        }

        const error = new CIError(
            (err as Error)?.message ?? 'Unknown error during GlobalSignOut',
            'cognito.globalsignout',
            ErrorSource.COGNITO,
            err
        );
        CILogger.getInstance().error('GlobalSignOut failed', error, { subjectId: authUser?.pk });
        Sentry?.captureException(error);
    }
}
